import { createRoot, Root } from 'react-dom/client';
import { createElement } from 'react';
import type { ChatOrchestrator } from './services/orchestrator';
import { ChatMessage, LlmMessage, ToolCallState, WidgetState } from './types';
import { McpChatWidgetHandle } from './interfaces/mcpChatWidgetHandle';
import { McpChatWidget } from './McpChatWidget';
import { McpChatWidgetProps } from './interfaces/mcpChatWidgetProps';

/**
 * Thin Web Component shell that mounts the McpChatWidget React FC into Shadow DOM.
 * Preserves the <mcp-chat-widget> custom element API for CDN / standalone usage.
 */
export class McpChatWidgetElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['primary-color', 'position', 'title', 'greeting', 'otp-greeting', 'language', 'server-url', 'auth', 'session-timeout', 'display-mode', 'widget-id', 'preview', 'portal-mode'];
  }

  private reactRoot: Root | null = null;
  private handleRef: { current: McpChatWidgetHandle | null } = { current: null };
  private disconnectToken = 0;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.disconnectToken += 1;
    if (!this.reactRoot) {
      this.reactRoot = createRoot(this.shadowRoot!);
    }
    this.renderReact();
  }

  disconnectedCallback(): void {
    const tokenAtDisconnect = ++this.disconnectToken;

    queueMicrotask(() => {
      if (this.isConnected) return;
      if (tokenAtDisconnect !== this.disconnectToken) return;

      this.reactRoot?.unmount();
      this.reactRoot = null;
      this.handleRef.current = null;
    });
  }

  attributeChangedCallback(_name: string, oldVal: string | null, newVal: string | null): void {
    if (oldVal === newVal) return;
    this.renderReact();
  }

  getOrchestrator(): ChatOrchestrator | null {
    return this.handleRef.current?.getOrchestrator() ?? null;
  }

  getMcpReadyPromise(): Promise<void> | null {
    return this.handleRef.current?.getMcpReadyPromise() ?? null;
  }

  resetConversation(): void {
    this.handleRef.current?.resetConversation();
  }

  async refreshConfig(): Promise<void> {
    await this.handleRef.current?.refreshConfig();
  }

  getAuthToken(): string | null {
    return this.handleRef.current?.getAuthToken() ?? null;
  }

  isAuthenticated(): boolean {
    return this.handleRef.current?.isAuthenticated() ?? false;
  }

  getConversationId(): string | null {
    return this.handleRef.current?.getConversationId() ?? null;
  }

  getWidgetTitle(): string {
    return this.handleRef.current?.getWidgetTitle() ?? '';
  }

  getUserEmail(): string | null {
    return this.handleRef.current?.getUserEmail() ?? null;
  }

  getDirection(): 'ltr' | 'rtl' {
    return this.handleRef.current?.getDirection() ?? 'rtl';
  }

  loadConversation(conversationId: string, messages: ChatMessage[], toolCalls?: ToolCallState[], historyMessages?: LlmMessage[]): void {
    this.handleRef.current?.loadConversation(conversationId, messages, toolCalls, historyMessages);
  }

  async logout(): Promise<void> {
    await this.handleRef.current?.logout();
  }

  private renderReact(): void {
    if (!this.reactRoot) return;

    const props: McpChatWidgetProps & { ref: { current: McpChatWidgetHandle | null } } = {
      shadow: this.shadowRoot!,
      primaryColor: this.getAttribute('primary-color'),
      position: this.getAttribute('position'),
      title: this.getAttribute('title'),
      greeting: this.getAttribute('greeting'),
      otpGreeting: this.getAttribute('otp-greeting'),
      language: this.getAttribute('language'),
      serverUrl: this.getAttribute('server-url'),
      auth: this.getAttribute('auth'),
      sessionTimeout: this.getAttribute('session-timeout'),
      displayMode: this.getAttribute('display-mode'),
      widgetId: this.getAttribute('widget-id'),
      preview: this.hasAttribute('preview'),
      portalMode: this.hasAttribute('portal-mode'),
      ref: this.handleRef,
    };

    this.reactRoot.render(createElement(McpChatWidget, props));
  }
}

export function shouldSkipReactRender(state: WidgetState): boolean {
  if (!state.isLoading) return false;
  const lastMessage = state.messages[state.messages.length - 1];
  return !!lastMessage?.isStreaming;
}
